import { useEffect, useState } from 'react'

export interface UseImagePreloadResult {
  ready: boolean
}

/**
 * Module-level cache of URLs whose `img.decode()` has resolved at least once
 * this session. A decoded image is in the browser cache and fully rasterized,
 * so any later mount of the same url can report ready synchronously with no
 * false-frame and no redundant Image construction.
 *
 * Rejected decodes are intentionally NOT added — a CORS/abort failure shouldn't
 * pin a url as "decoded" for the rest of the session.
 */
const decodedUrls = new Set<string>()

/**
 * Preloads a single image URL and reports when the browser is ready to render
 * it without a network round-trip OR a synchronous decode hitch. Extracted for
 * reuse across the Oracle animation components (tarot card flip, oracle card
 * landing) which all need to gate a CSS transition on "the image is decoded and
 * in cache".
 *
 * Behavior:
 * - When `enabled` is false or `url` is falsy, no Image is constructed and
 *   `ready` stays false. Callers use this to skip preloading until they
 *   actually need the asset.
 * - When both are set and the url has NOT been decoded this session, constructs
 *   `new Image()`, assigns `src`, and calls `img.decode()`. `ready` stays false
 *   until the decode promise RESOLVES (the gate is decode(), not onload). On
 *   resolve, the url is added to the module-level decoded cache.
 * - If `decode()` REJECTS (CORS/abort), `ready` still flips true so a failure
 *   cannot stall the animation, but the url is NOT cached.
 * - When the url is already in the decoded cache, `ready` is true synchronously
 *   on first render and no new Image is constructed.
 * - Fallback: if `img.decode` is not a function (older browsers), falls back to
 *   the `onload`/`onerror` gate, flipping ready synchronously if `img.complete`
 *   is already true.
 */
export const useImagePreload = (
  url: string | null | undefined,
  enabled: boolean,
): UseImagePreloadResult => {
  // Lazy init: a cache hit yields a synchronous `true` with no false-frame.
  const [ready, setReady] = useState(
    () => enabled && !!url && decodedUrls.has(url),
  )

  useEffect(() => {
    if (!enabled || !url) {
      // Reset so a previous true value doesn't leak across an enable/disable
      // toggle or a url -> null transition.
      setReady(false)
      return undefined
    }

    // Cache hit: already decoded this session — ready immediately, no Image.
    if (decodedUrls.has(url)) {
      setReady(true)
      return undefined
    }

    setReady(false)

    // Cleanup guard so a stale decode (after url changes / unmount / disable)
    // doesn't flip ready while the new url is still pending.
    let cancelled = false
    const img = new Image()
    img.src = url

    const { decode } = (img as { decode?: () => Promise<void> })

    if (typeof decode === 'function') {
      decode.call(img)
        .then(() => {
          if (cancelled) return
          decodedUrls.add(url)
          setReady(true)
        })
        .catch(() => {
          // Never stall the animation on a decode failure, but do NOT cache.
          if (!cancelled) setReady(true)
        })
      return () => { cancelled = true }
    }

    // Fallback for environments without HTMLImageElement.decode: gate on
    // onload/onerror. Treat load errors as 'done' so a 404/CORS/DNS failure
    // cannot stall the animation; downstream <img> tags have their own
    // onError fallbacks that show a placeholder.
    const markReady = () => {
      if (!cancelled) setReady(true)
    }
    img.onload = markReady
    img.onerror = markReady

    // If the browser already has the image cached, `complete` is true at
    // construction and `onload` will never fire — flip ready synchronously.
    if (img.complete) {
      markReady()
    }

    return () => { cancelled = true }
  }, [url, enabled])

  return { ready }
}
